import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTasks, useCreateTask } from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  type CreateForm,
  STATUS_LABELS,
  STATUS_VARIANTS,
  PRIORITY_LABELS,
  PRIORITY_VARIANTS,
  CATEGORY_LABELS,
  createFormSchema,
  Th,
  Td,
  PaginationButton,
  Field,
  Skeleton,
  inputCss,
  primaryBtn,
  secondaryBtn,
  modalOverlay,
  modalStyle,
} from './task-list.support';
import { onlyIf, usePermissions } from '../../../platform/auth/permissions';

let idCounter = 0;

export function TaskList() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(() => searchParams.has('nova'));

  const params: Record<string, unknown> = { page, limit: 20 };
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;
  if (priorityFilter) params.priority = priorityFilter;

  const { data, isLoading, isError } = useTasks(params);
  const createTask = useCreateTask();

  const tasks = asList(data);
  const meta = getMeta(data);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusFilter = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handlePriorityFilter = useCallback((value: string) => {
    setPriorityFilter(value);
    setPage(1);
  }, []);

  const form = useForm<CreateForm>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      title: '',
      priority: 'MEDIUM',
      category: 'OTHER',
      description: '',
      dueDate: '',
    },
  });

  function handleCreate(data: CreateForm) {
    createTask.mutate({
      ...data,
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined,
    });
    setShowCreate(false);
    form.reset();
  }

  function fmtDate(v: unknown): string {
    if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
    if (v instanceof Date) return v.toISOString().substring(0, 10);
    return '—';
  }

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="Tarefas" subtitle="Escalas e afazeres da casa" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            Não foi possível carregar a lista de tarefas.
          </p>
        </Section>
      </div>
    );
  }

  const totalPages = Number(meta.totalPages) || 1;

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="Tarefas"
        subtitle={`${str(meta.total)} tarefas registradas`}
        action={onlyIf(can('tasks.create'), {
          label: '+ Nova tarefa',
          onClick: () => {
            setShowCreate(true);
          },
        })}
      />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar por título..."
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          style={{
            flex: 1,
            maxWidth: 300,
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            handleStatusFilter(e.target.value);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
            minWidth: 160,
          }}
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => {
            handlePriorityFilter(e.target.value);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
            minWidth: 140,
          }}
        >
          <option value="">Todas as prioridades</option>
          {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Skeleton */}
      {isLoading && <Skeleton />}

      {/* Tabela */}
      {!isLoading && (
        <Section title="Tarefas">
          {tasks.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: 'var(--faint)',
                fontSize: '0.875rem',
              }}
            >
              {search || statusFilter || priorityFilter
                ? 'Nenhuma tarefa encontrada com esses filtros.'
                : 'Nenhuma tarefa registrada ainda.'}
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--line-soft)' }}>
                    <Th>Título</Th>
                    <Th>Categoria</Th>
                    <Th>Prioridade</Th>
                    <Th>Vencimento</Th>
                    <Th>Situação</Th>
                    <Th>Atrib.</Th>
                    <Th>Lista de verificação</Th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((raw) => {
                    const t = asRecord(raw);
                    const st = str(t.status);
                    const pr = str(t.priority);
                    const cat = str(t.category);
                    const cnt = t._count as Record<string, unknown> | undefined;
                    return (
                      <tr
                        key={str(t.id, String(idCounter++))}
                        onClick={() => {
                          void navigate(`/tarefas/${String(t.id)}`);
                        }}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--soft)',
                          transition: 'background 0.15s',
                        }}
                      >
                        <Td>
                          <span style={{ fontWeight: 500, color: 'var(--green)' }}>
                            {str(t.title)}
                          </span>
                        </Td>
                        <Td>
                          <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                            {CATEGORY_LABELS[cat] ?? cat}
                          </span>
                        </Td>
                        <Td>
                          <Badge
                            label={PRIORITY_LABELS[pr] ?? pr}
                            variant={PRIORITY_VARIANTS[pr] ?? 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {fmtDate(t.dueDate)}
                        </Td>
                        <Td>
                          <Badge
                            label={STATUS_LABELS[st] ?? st}
                            variant={STATUS_VARIANTS[st] ?? 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {(cnt?.assignments ?? 0) as number}
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {(cnt?.checklistItems ?? 0) as number}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Paginação */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem',
                  }}
                >
                  <PaginationButton
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(page - 1);
                    }}
                    label="<"
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <PaginationButton
                      key={p}
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                      }}
                      label={String(p)}
                    />
                  ))}
                  <PaginationButton
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                    label=">"
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {/* Modal: Nova tarefa */}
      {showCreate && (
        <div
          style={modalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreate(false);
            }
          }}
        >
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Nova tarefa</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Field label="Título" error={form.formState.errors.title?.message}>
                <input {...form.register('title')} style={inputCss} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Field label="Categoria">
                  <select {...form.register('category')} style={inputCss}>
                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Prioridade">
                  <select {...form.register('priority')} style={inputCss}>
                    {Object.entries(PRIORITY_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Descrição">
                <textarea
                  {...form.register('description')}
                  style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
                />
              </Field>
              <Field label="Vencimento">
                <input {...form.register('dueDate')} type="date" style={inputCss} />
              </Field>
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  justifyContent: 'flex-end',
                  marginTop: '0.5rem',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    form.reset();
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void form.handleSubmit(handleCreate)();
                  }}
                  disabled={createTask.isPending}
                  style={primaryBtn}
                >
                  {createTask.isPending ? 'Criando...' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
